import type { AxiosRequestConfig, Method } from 'axios'
import { decodeJwt } from 'jose'
import { filter, map, switchMap, BehaviorSubject, firstValueFrom, delayWhen } from 'rxjs'
import { fromFetch } from 'rxjs/fetch'
import LocalStorageService from './storage-service.js';
import { DOMAIN_API_KEY} from './definitions.js';
import { DateTime } from 'luxon'
import { Context, RaissoError, ServiceResponse, errorFactory } from './responses.js';
import * as events from './events.js';
import { autoSignIn } from './auto-sign-in.js';
import { dispatchEvent } from './utils.js';
import { TokenController, GenericResponse } from './controllers/index.js';
import { Result, R } from '@mobily/ts-belt'
import { USER_DETAILS_URL } from './controllers/user.js'
import { LOGIN_BASE_URL } from './controllers/login.js'
import { RaiSsoProfile } from './profile.js'

const localStorageService = LocalStorageService.getService();

const DEBUG = process.env.NODE_ENV === 'development';

function log(message: string) {
  if (DEBUG) {
    console.log('[RAISSO] ' + message);
  }
}

/**
 * Funzione per il check del token sul server
 */
async function checkTokenFromServer(): ServiceResponse<void> {
  const refreshToken = localStorageService.getRefreshToken();
  if (!refreshToken) {
    console.error('[raisso] checkTokenFromServer: Refresh token non presente')
    return R.Error(errorFactory(RaissoError, 'Refresh token non presente', { context: { needLogin: true }}));
  }
  const accessToken = localStorageService.getAccessToken();
  if (!accessToken) {
    return refreshTokenRequest();
  }

  try {
    const response = await TokenController.instance.check({ domainApiKey: DOMAIN_API_KEY, refreshToken, accessToken })
    R.tap(response, (respData) => {
      const { token: newToken, user_update, user_data_update } = respData
      if (newToken) {
        localStorageService.setToken({ authToken: newToken, refreshToken });
        localStorageService.setTokenLastDate();
      }
      if (user_update || user_data_update) {
        let profile = localStorageService.getProfile()
        if (profile) {
          const json = profile.toJSON();
          json.user_update = user_update;
          json.user_data_update = user_data_update;
          profile = RaiSsoProfile.fromJSON(json);
          localStorageService.setProfile(profile);
          if (events.userUpdate) {
            events.event$.next(new CustomEvent(events.userUpdate, { detail: profile, bubbles: true, cancelable: true }))
            dispatchEvent(events.userUpdate, profile);
          }
          if (events.userDataUpdate) {
            events.event$.next(new CustomEvent(events.userDataUpdate, { detail: profile, bubbles: true, cancelable: true }))
            dispatchEvent(events.userDataUpdate, profile);
          }
        }
      }
    });
    if (R.isError(response)) {
      return response;
    }
    return R.fromPromise(Promise.resolve())
  } catch (e) {
    if (e instanceof RaissoError) {
      return R.Error(e)
    }
    console.error('[raisso] Error:', e);
    if (e instanceof Error) {
      return R.Error(errorFactory(RaissoError, e.message, { cause: e, context: { needLogin: true }}));
    }
    return R.Error(errorFactory(RaissoError, 'Errore nella chiamata al servizio di check-token', { cause: e, context: { needLogin: true } }));
  }
}
/**
 * Funzione per il refresh del token dal server
 */
async function refreshTokenRequest(): ServiceResponse<void> {
  try {
    const refreshToken = localStorageService.getRefreshToken();
    if (!refreshToken) {
      console.error('[raisso] refreshTokenRequest: Refresh token non valido')
      return R.Error(errorFactory(RaissoError, 'Refresh token non valido'));
    }
    const response = await TokenController.instance.refresh({ domainApiKey: DOMAIN_API_KEY, refreshToken })
    R.tap(response, resp => {
      const { token, user_update, user_data_update } = resp
      if (!token) {
        throw errorFactory(RaissoError, 'Errore nella chiamata al servizio di refresh-token');
      }
      localStorageService.setToken({ authToken: token, refreshToken });
      localStorageService.setTokenLastDate()
      if ((user_update?.length ?? 0) > 0 || user_update) {
        const json = localStorageService.getProfile()?.toJSON();
        if (json) {
          json.user_update = user_update;
          json.user_data_update = user_data_update;
          const profile = RaiSsoProfile.fromJSON(json);
          localStorageService.setProfile(profile);
          if (user_update) {
            events.event$.next(new CustomEvent(events.userUpdate, { detail: profile, bubbles: true, cancelable: true }))
            dispatchEvent(events.userUpdate, profile);
          }
          if (user_data_update) {
            events.event$.next(new CustomEvent(events.userDataUpdate, { detail: profile, bubbles: true, cancelable: true }))
            dispatchEvent(events.userDataUpdate, profile);
          }
        }
      }
    });
    return R.isError(response) ? response : R.fromPromise(Promise.resolve());
  } catch (e) {
    if (e instanceof RaissoError) {
      return R.Error(e);
    }
    console.error('[raisso] refreshTokenRequest: ', e);
    if (e instanceof Error) {
      return R.Error(errorFactory(RaissoError, e.message, { cause: e }));
    }
    return R.Error(errorFactory(RaissoError, 'Errore nella chiamata al servizio di refresh-token', { context: { needLogin: true } }));
  }
}

function checkCanCallAuthService(request: Request) {
  const profile = localStorageService.getProfile();
  const { url } = request;
  if (profile && profile.user_type === 'ente'
    && (url !== USER_DETAILS_URL)
    && !url.includes(LOGIN_BASE_URL)) {
    return false;
  }
  return true
}

function isTokenInSameDay() {
  const lastTokenTimestamp = localStorageService.getTokenLastDate();
  if (!lastTokenTimestamp) return false;
  const lastTokenDay = DateTime.fromMillis(lastTokenTimestamp).toFormat("yyyyMMdd");
  const nowDay = DateTime.now().toFormat("yyyyMMdd");
  return (nowDay === lastTokenDay);
}

function checkAccessTokenIsValid() {
  const accessToken = localStorageService.getAccessToken();
  if (!accessToken) {
    return false;
  }
  const { exp } = decodeJwt(accessToken)

  if (!isTokenInSameDay() || (exp ?? Number.POSITIVE_INFINITY) < (Date.now() / 1e3 + 60 * 5)) {
    return false
  }
  return true
}

function setRequestHeaders(req: Request): Request {
  req.headers.set('domainApiKey', DOMAIN_API_KEY);
  if (!req.headers.has('X-Auth-Token')) {
    const token = localStorageService.getAccessToken()
    if (!token) {
      throw new RaissoError('Access token non presente')
    }
    req.headers.set('X-Auth-Token', token);
  }
  req.headers.set('X-Caller', 'web')
  req.headers.set('X-Caller-Version', '1.0')
  return req;
}

class Raisso {

  get isMinor() {
    const p = localStorageService.getProfile();
    return !!p?.age && p.age <= 18;
  }

  get events() {
    return events;
  }

  get isEnte() {
    const p = localStorageService.getProfile();
    return p && p.user_type === 'ente';
  }

  get domainApiKey() {
    return DOMAIN_API_KEY;
  }

  private static _instance?: Raisso;
  public static get instance() {
    if (!Raisso._instance) {
      Raisso._instance = new Raisso()
    }
    return Raisso._instance;
  }

  private isTokenRefreshed = false;
  private _isTokenChecking$ = new BehaviorSubject(false);

  private get isTokenChecking() {
    return this._isTokenChecking$.value;
  }
  private set isTokenChecking(val) {
    this._isTokenChecking$.next(val);
  }

  private constructor() {/* empty */}

  public async tryAutoLogin(){
    if(!this.checkLogin()) {
      await autoSignIn.tryLogin();
    }
  }

  async callAuthService<T>(
      url: string,
      method: Method = "GET",
      data?: string | object,
      config?: Pick<AxiosRequestConfig, "baseURL" | "headers" | "params" | "timeout" | "responseType" | "maxContentLength">
  ) {
    if (!this.checkLogin()) {
      return errorFactory(RaissoError, 'User not logged in', { context: { needLogin: true} })
    }

    const contentType = config?.headers?.['Content-Type'] ?? 'application/json';

    let body: string | undefined;
    if (typeof data === 'string' || data == null) {
      body = data
    } else if (contentType === 'application/json') {
      body = JSON.stringify(data)
    } else {
      body = data.toString()
    }

    const requestInit: RequestInit = {
      method,
      credentials: 'omit',
      cache: 'no-cache',
      headers: {
        'Accept': 'application/json',
        ...(data ? { 'Content-Type': 'application/json' }: {}),
        ...Object.entries(config?.headers ?? {}).reduce((h, [k, v]) => {
          if (v) { h[k] = `${v}` }
          return h;
        }, {} as Record<string, string>),

      },
      referrerPolicy: 'origin-when-cross-origin',
      body,
    };

    let href = `${config?.baseURL ?? ''}${url}`;
    if (config?.params) {
      const search = new URLSearchParams(config.params)
      href += '?' + search.toString()
    }
    const resp = await this.callAuthFetch<T>(href, requestInit);
    return R.getExn(resp);
  }

  async callAuthFetch<T>(url: RequestInfo | URL, requestInit?: RequestInit, type?: 'json'): Promise<Result<T, RaissoError>>;
  async callAuthFetch(url: RequestInfo | URL, requestInit?: RequestInit, type?: 'text'): Promise<Result<string, RaissoError>>;
  async callAuthFetch<T>(url: RequestInfo | URL, requestInit?: RequestInit, type: 'json' | 'text' = 'json'): Promise<Result<T | string, RaissoError>> {
    if (!this.checkLogin()) {
      return R.Error(errorFactory(RaissoError, 'Utente non loggato', { context: { needLogin: true }}));
    }

    const request = new Request(url, requestInit);

    if (!checkCanCallAuthService(request)) {
      return R.Error(errorFactory(RaissoError, "L'utente non è abilitato a chiamare il servizio", { context: { request: { url: request.url, body: (await request.clone().json()) }}}));
    }

    if (!checkAccessTokenIsValid() && !this.isTokenChecking) {
      log('check: ' + request.url)
      this.isTokenChecking = true;
      try {
        const resp = await checkTokenFromServer();
        R.tapError(resp,
          error => {
            if (error.context && typeof error.context === 'object' &&  'needLogin' in error.context) {
              const needLogin = error.context.needLogin;
              if (needLogin) {
                this.softLogout(true, true)
              }
            }
            return R.fromPromise(Promise.resolve())
          });
        if (R.isError(resp)) {
          return resp;
        }
      } catch (e) {
        this.softLogout(true, false)
        if (e instanceof RaissoError) {
          return R.Error(e)
        }
        console.error(e);
        return R.Error(errorFactory(RaissoError, ((e instanceof Error) ? e.message : '') ?? 'L\'utente deve effettuare la login', { cause: e, context: { needLogin: true }}))
      } finally {
        this.isTokenChecking = false
      }
    }

    const requestInput$ = new BehaviorSubject<Request>(request);

    const request$ = requestInput$.pipe(
      delayWhen(() => this._isTokenChecking$.pipe(filter(val => !val))),
      map(setRequestHeaders),
      map(req => new Request(req.clone(), {
        credentials: 'omit',
        cache: 'no-cache',
      })),
      switchMap(req => fromFetch(req)),
      switchMap(async (response) => {
        if (!response.ok) {
          const resp: GenericResponse = await response.json()
          const ctx: Context = {
            request: { url: request.url, body: (await request.clone().text()) },
            response: { statusText: response.statusText, body: JSON.stringify(resp)  } }
          if (response.status === 401) {
            ctx.needRefresh = true;
          }
          const error: RaissoError = errorFactory(RaissoError, resp, { context: ctx })
          return R.Error(error);
        } else {
          const resp = response.clone()
          return R.Ok<T | string>(type === 'json' ? (await resp.json()) : (await resp.text()))
        }
      })
    );

    try {
      let resp = await firstValueFrom(request$);
      if (R.isError(resp)) {
        const error = resp._0;
        if (error.context?.needRefresh && !this.isTokenRefreshed) {
          this.isTokenChecking = true;
          try {
            const r = await refreshTokenRequest();
            if (R.isError(r)) {
              return resp;
            }
          } finally {
            this.isTokenChecking = false;
          }
          requestInput$.next(request);
          const data = await firstValueFrom(request$);
          resp = data;
        }
      }
      return resp;
    } catch (e) {
      if (e instanceof RaissoError) {
        return R.Error(errorFactory(RaissoError, e.message, { cause: e }))
      }
      console.error(e);
      return R.Error(errorFactory(RaissoError, 'Errore nella chiamata al servizio ' + request.url, {
        cause: e,
        context: { request: { url: request.url, body: (await request.clone().text()) }}
      }));
    }
  }

  checkLogin(): boolean {
    const profile = localStorageService.getProfile();
    const authToken = localStorageService.getAccessToken();
    const refreshToken = localStorageService.getRefreshToken();
    if (!authToken || !refreshToken) {
      if (profile) {
        this.softLogout(false,false)
      }
      return false;
    }
    const { exp } = decodeJwt(authToken);
    if (exp && profile && profile.expirationDate.getTime() < exp * 1e3) {
      profile.renewExpiration(exp * 1e3);
      localStorageService.setProfile(profile);
    }
    return true;
  }

  checkPrivacy(){
    if(this.checkLogin()) {
      const profile = localStorageService.getProfile();
      return !!profile?.privacyPolicyIsAccepted;
    }
    return false;
  }

  softLogout(emitEvent: boolean, preventAutologin?: boolean) {
    if (this.isEnte) {
      throw errorFactory(RaissoError, 'L\'utente di tipo ente non può fare logout');
    }
    localStorageService.clearToken();
    localStorageService.clearProfile();
    try{
      window.gigya.socialize.logout();
    } catch(e) {
      // pass
    }
    if (emitEvent) {
      dispatchEvent(events.logout, undefined);
    }
    if (preventAutologin) {
      autoSignIn.preventSilentLogin().catch(() => {/* empty */})
    }
    return true;
  }

  refresh = refreshTokenRequest.bind(this);
}

const raisso = Raisso.instance;

export { raisso, type Raisso, type RaissoError };
